import { useEffect, useRef } from 'react';
import { useEditMode } from '../../context/EditModeContext';
import { useBuilder } from '../../context/BuilderContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getBlock } from '../../utils/blockRegistry';
import BlockControls from '../BlockControls/BlockControls';
import styles from './BlockWrapper.module.css';

function buildInlineStyle(blockStyle) {
  if (!blockStyle) return {};
  const style = {};
  const map = {
    paddingTop: 'paddingTop',
    paddingBottom: 'paddingBottom',
    paddingLeft: 'paddingLeft',
    paddingRight: 'paddingRight',
    marginTop: 'marginTop',
    marginRight: 'marginRight',
    marginBottom: 'marginBottom',
    marginLeft: 'marginLeft',
    backgroundColor: 'backgroundColor',
    fontSize: 'fontSize',
    fontFamily: 'fontFamily',
    borderRadius: 'borderRadius',
    maxWidth: 'maxWidth',
    alignSelf: 'alignSelf',
  };
  for (const [key, cssProp] of Object.entries(map)) {
    if (blockStyle[key] !== undefined && blockStyle[key] !== '') {
      style[cssProp] = blockStyle[key];
    }
  }
  // Override CSS custom properties so children using var(--text-body) etc. pick up the color
  if (blockStyle.textColor) {
    style.color = blockStyle.textColor;
    style['--text-body'] = blockStyle.textColor;
    style['--text-heading'] = blockStyle.textColor;
    style['--text-muted'] = blockStyle.textColor;
  }
  return style;
}

function BlockWrapper({ block, pageKey, blockIndex, totalBlocks }) {
  const { editMode } = useEditMode();
  const { selectedBlockId, selectBlock } = useBuilder();
  const blockDef = getBlock(block.type);
  const isEditing = editMode !== 'off';
  const isSelected = isEditing && selectedBlockId === block.id;
  const wrapperRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!isSelected || !isMobile || !wrapperRef.current) return;
    // App.js reserves the sheet's `half`-snap height as scroll-padding-bottom
    // on the document while editing on mobile, so centring within that
    // scroll-padding-reduced scrollport keeps the block in the visible band
    // above the sheet instead of behind it. The scroll itself always
    // happens; only the animation is suppressed for users who asked for
    // reduced motion (matchMedia is guarded — jsdom provides it inconsistently).
    const prefersReducedMotion =
      typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    wrapperRef.current.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
    // blockIndex is intentionally in the deps: it also fires when a block
    // ABOVE this one is inserted/removed and only shifts this block's index
    // without the user moving it. That is accepted deliberately — the
    // selected block's on-screen position moved either way, and following it
    // is the same "keep what's selected in view" contract as the initial
    // selection scroll. Tracking the previous index in a ref to suppress
    // that case was considered and rejected: it adds state for a distinction
    // (moved vs. shifted) users don't perceive differently on screen.
  }, [isSelected, isMobile, blockIndex]);

  if (!blockDef) return null;

  const BlockComponent = blockDef.component;

  const handleClick = (e) => {
    if (!isEditing) return;
    e.stopPropagation();
    selectBlock(block.id, pageKey);
  };

  return (
    <div
      ref={wrapperRef}
      className={`${styles.wrapper} ${isEditing ? styles.editable : ''} ${isSelected ? styles.selected : ''}`}
      style={buildInlineStyle(block.style)}
      onClick={handleClick}
      data-block-id={block.id}
      data-block-type={block.type}
      data-testid={`block-${block.id}`}
    >
      {isSelected && (
        <>
          <div className={styles.typeLabel}>{blockDef.label}</div>
          <BlockControls
            block={block}
            pageKey={pageKey}
            blockIndex={blockIndex}
            totalBlocks={totalBlocks}
          />
        </>
      )}
      <BlockComponent {...block.props} />
    </div>
  );
}

export default BlockWrapper;
